import React, { useEffect, useState, useCallback } from "react";
import { cloneDeep, set as lset } from "lodash";
import { ShieldCheck, LogOut, Save, RotateCcw, Plus, Trash2, Check, Loader2, ArrowLeft, Inbox, FileText, Upload } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { LOGO_URL } from "@/components/Nav";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const API_ROOT = process.env.REACT_APP_BACKEND_URL;

const ImageField = ({ label, value, onChange }) => {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(false);
  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    setErr(false);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const res = await fetch(`${API}/upload`, { method: "POST", credentials: "include", body: fd });
      if (!res.ok) throw new Error();
      const d = await res.json();
      onChange(`${API_ROOT}${d.url}`);
    } catch {
      setErr(true);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div>
      <span className="text-xs font-semibold uppercase tracking-wider text-[#4A6D56]">{label}</span>
      <div className="mt-2 flex items-center gap-4">
        <img src={value} alt="" className="h-16 w-24 object-cover rounded-lg border border-[#D1D8D3] bg-[#EEF2EE]" />
        <label className="inline-flex items-center gap-2 rounded-full border border-[#1A3B2A] text-[#1A3B2A] px-4 py-2 text-sm font-semibold cursor-pointer hover:bg-[#1A3B2A]/5 transition-colors">
          {busy ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />} Byt bild
          <input type="file" accept="image/*" className="hidden" onChange={onFile} data-testid={`upload-${label}`} />
        </label>
        {err && <span className="text-xs text-[#c0392b]">Uppladdning misslyckades</span>}
      </div>
    </div>
  );
};

const Field = ({ label, value, onChange, area, ...rest }) => (
  <label className="block">
    <span className="text-xs font-semibold uppercase tracking-wider text-[#4A6D56]">{label}</span>
    {area ? (
      <textarea
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        rows={3}
        className="mt-1 w-full rounded-xl border border-[#D1D8D3] bg-white px-3 py-2 text-sm outline-none focus:border-[#1A3B2A] transition-colors resize-none"
        {...rest}
      />
    ) : (
      <input
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full rounded-xl border border-[#D1D8D3] bg-white px-3 py-2 text-sm outline-none focus:border-[#1A3B2A] transition-colors"
        {...rest}
      />
    )}
  </label>
);

const Card = ({ title, children }) => (
  <div className="rounded-2xl border border-[#D1D8D3] bg-white p-6 mb-5">
    <h3 className="font-display text-2xl text-[#1A3B2A] mb-4">{title}</h3>
    <div className="grid gap-4">{children}</div>
  </div>
);

const ContentEditor = () => {
  const [content, setContent] = useState(null);
  const [status, setStatus] = useState("loading");

  useEffect(() => {
    fetch(`${API}/content`)
      .then((r) => r.json())
      .then((d) => {
        setContent(d);
        setStatus("idle");
      })
      .catch(() => setStatus("error"));
  }, []);

  const upd = (path, value) => setContent((c) => lset(cloneDeep(c), path, value));

  const pushTo = (path, item) =>
    setContent((c) => {
      const n = cloneDeep(c);
      const arr = path.split(".").reduce((o, k) => o[k], n);
      arr.push(item);
      return n;
    });
  const removeAt = (path, idx) =>
    setContent((c) => {
      const n = cloneDeep(c);
      const arr = path.split(".").reduce((o, k) => o[k], n);
      arr.splice(idx, 1);
      return n;
    });

  const save = async () => {
    setStatus("saving");
    try {
      const res = await fetch(`${API}/content`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(content),
      });
      if (!res.ok) throw new Error();
      setStatus("saved");
      setTimeout(() => setStatus("idle"), 2000);
    } catch {
      setStatus("error");
    }
  };

  const reset = async () => {
    if (!window.confirm("Återställ allt innehåll till standardtexterna?")) return;
    const res = await fetch(`${API}/content/reset`, { method: "POST", credentials: "include" });
    if (res.ok) setContent(await res.json());
  };

  if (status === "loading" || !content)
    return <p className="text-[#4A6D56] flex items-center gap-2"><Loader2 className="animate-spin" size={18} /> Laddar innehåll…</p>;

  return (
    <div>
      <div className="sticky top-4 z-10 flex items-center justify-between mb-6 rounded-2xl bg-[#1A3B2A] text-[#F7F7F4] px-5 py-3">
        <span className="text-sm font-medium">Redigera webbplatsens texter</span>
        <div className="flex items-center gap-2">
          <button data-testid="admin-reset-btn" onClick={reset} className="inline-flex items-center gap-2 rounded-full border border-white/25 px-4 py-2 text-sm hover:bg-white/10 transition-colors">
            <RotateCcw size={15} /> Återställ
          </button>
          <button data-testid="admin-save-btn" onClick={save} disabled={status === "saving"} className="inline-flex items-center gap-2 rounded-full bg-[#F7F7F4] text-[#1A3B2A] px-5 py-2 text-sm font-bold hover:bg-[#C2D3C4] active:scale-95 transition-[background-color,transform] disabled:opacity-60">
            {status === "saving" ? <Loader2 size={15} className="animate-spin" /> : status === "saved" ? <Check size={15} /> : <Save size={15} />}
            {status === "saved" ? "Sparat!" : "Spara"}
          </button>
        </div>
      </div>

      {status === "error" && <p className="text-[#c0392b] mb-4 text-sm">Något gick fel vid sparande.</p>}

      <Card title="Allmänt">
        <Field label="Föreningens namn" value={content.brand} onChange={(v) => upd("brand", v)} />
        <Field label="Grundades år" value={content.since} onChange={(v) => upd("since", v)} />
        <Field label="Löpande band (kommaseparerat)" value={content.marquee.join(", ")} onChange={(v) => upd("marquee", v.split(",").map((s) => s.trim()).filter(Boolean))} />
      </Card>

      <Card title="Startbild (Hero)">
        <ImageField label="Bakgrundsbild" value={content.hero.image} onChange={(v) => upd("hero.image", v)} />
        <Field label="Överrubrik" value={content.hero.kicker} onChange={(v) => upd("hero.kicker", v)} />
        <div className="grid grid-cols-3 gap-3">
          {content.hero.lines.map((l, i) => (
            <Field key={i} label={`Rubrikrad ${i + 1}`} value={l} onChange={(v) => upd(`hero.lines.${i}`, v)} />
          ))}
        </div>
        <Field label="Ingress" area value={content.hero.subtitle} onChange={(v) => upd("hero.subtitle", v)} />
        <div className="grid grid-cols-2 gap-3">
          <Field label="Knapp 1" value={content.hero.cta} onChange={(v) => upd("hero.cta", v)} />
          <Field label="Knapp 2" value={content.hero.ctaSecondary} onChange={(v) => upd("hero.ctaSecondary", v)} />
        </div>
      </Card>

      <Card title="Så funkar det">
        <Field label="Överrubrik" value={content.manifesto.kicker} onChange={(v) => upd("manifesto.kicker", v)} />
        <Field label="Rubrik" value={content.manifesto.title} onChange={(v) => upd("manifesto.title", v)} />
        {content.manifesto.steps.map((s, i) => (
          <div key={i} className="rounded-xl border border-[#E2E8E4] p-4 grid gap-3">
            <div className="flex items-center justify-between">
              <span className="text-sm font-semibold text-[#1A3B2A]">Steg {i + 1}</span>
              <button onClick={() => removeAt("manifesto.steps", i)} className="text-[#c0392b]"><Trash2 size={16} /></button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Nummer" value={s.n} onChange={(v) => upd(`manifesto.steps.${i}.n`, v)} />
              <Field label="Titel" value={s.title} onChange={(v) => upd(`manifesto.steps.${i}.title`, v)} />
            </div>
            <Field label="Text" area value={s.text} onChange={(v) => upd(`manifesto.steps.${i}.text`, v)} />
          </div>
        ))}
        <button onClick={() => pushTo("manifesto.steps", { n: "0" + (content.manifesto.steps.length + 1), title: "", text: "" })} className="inline-flex items-center gap-2 text-sm text-[#1A3B2A] font-semibold"><Plus size={15} /> Lägg till steg</button>
      </Card>

      <Card title="Bilen">
        <ImageField label="Bild på bilen" value={content.car.image} onChange={(v) => upd("car.image", v)} />
        <Field label="Överrubrik" value={content.car.kicker} onChange={(v) => upd("car.kicker", v)} />
        <div className="grid grid-cols-3 gap-3">
          <Field label="Modell" value={content.car.title} onChange={(v) => upd("car.title", v)} />
          <Field label="Årsmodell" value={content.car.year} onChange={(v) => upd("car.year", v)} />
          <Field label="Bränsle" value={content.car.fuel} onChange={(v) => upd("car.fuel", v)} />
        </div>
        <Field label="Text" area value={content.car.text} onChange={(v) => upd("car.text", v)} />
        <Field label="Utrustning (kommaseparerat)" value={content.car.specs.join(", ")} onChange={(v) => upd("car.specs", v.split(",").map((s) => s.trim()).filter(Boolean))} />
      </Card>

      <Card title="Priser">
        <Field label="Överrubrik" value={content.pricing.kicker} onChange={(v) => upd("pricing.kicker", v)} />
        <Field label="Rubrik" value={content.pricing.title} onChange={(v) => upd("pricing.title", v)} />
        {["join", "usage"].map((key) => (
          <div key={key}>
            <p className="text-sm font-semibold text-[#1A3B2A] mb-2">{key === "join" ? "Att gå med" : "När du kör"}</p>
            {content.pricing[key].map((r, i) => (
              <div key={i} className="rounded-xl border border-[#E2E8E4] p-3 grid grid-cols-[1fr_1fr_1fr_auto] gap-2 items-end mb-2">
                <Field label="Rad" value={r.label} onChange={(v) => upd(`pricing.${key}.${i}.label`, v)} />
                <Field label="Pris" value={r.value} onChange={(v) => upd(`pricing.${key}.${i}.value`, v)} />
                <Field label="Notering" value={r.note} onChange={(v) => upd(`pricing.${key}.${i}.note`, v)} />
                <button onClick={() => removeAt(`pricing.${key}`, i)} className="text-[#c0392b] pb-2"><Trash2 size={16} /></button>
              </div>
            ))}
            <button onClick={() => pushTo(`pricing.${key}`, { label: "", value: "", note: "" })} className="inline-flex items-center gap-2 text-sm text-[#1A3B2A] font-semibold"><Plus size={15} /> Lägg till rad</button>
          </div>
        ))}
      </Card>

      <Card title="Studenterbjudande">
        <ImageField label="Bild" value={content.student.image} onChange={(v) => upd("student.image", v)} />
        <Field label="Överrubrik" value={content.student.kicker} onChange={(v) => upd("student.kicker", v)} />
        <Field label="Rubrik" value={content.student.title} onChange={(v) => upd("student.title", v)} />
        <Field label="Text" area value={content.student.text} onChange={(v) => upd("student.text", v)} />
        <Field label="Framhävd text" value={content.student.highlight} onChange={(v) => upd("student.highlight", v)} />
      </Card>

      <Card title="Hållbarhet">
        <ImageField label="Bild" value={content.sustainability.image} onChange={(v) => upd("sustainability.image", v)} />
        <Field label="Överrubrik" value={content.sustainability.kicker} onChange={(v) => upd("sustainability.kicker", v)} />
        <Field label="Rubrik" value={content.sustainability.title} onChange={(v) => upd("sustainability.title", v)} />
        <Field label="Text" area value={content.sustainability.text} onChange={(v) => upd("sustainability.text", v)} />
        <div className="grid grid-cols-3 gap-3">
          {content.sustainability.stats.map((s, i) => (
            <div key={i} className="grid gap-2">
              <Field label="Siffra" value={s.value} onChange={(v) => upd(`sustainability.stats.${i}.value`, v)} />
              <Field label="Etikett" value={s.label} onChange={(v) => upd(`sustainability.stats.${i}.label`, v)} />
            </div>
          ))}
        </div>
      </Card>

      <Card title="Kontakt">
        <Field label="Överrubrik" value={content.contact.kicker} onChange={(v) => upd("contact.kicker", v)} />
        <Field label="Rubrik" value={content.contact.title} onChange={(v) => upd("contact.title", v)} />
        {content.contact.people.map((p, i) => (
          <div key={i} className="rounded-xl border border-[#E2E8E4] p-4 grid gap-3">
            <div className="flex items-center justify-between">
              <span className="text-sm font-semibold text-[#1A3B2A]">Kontaktperson {i + 1}</span>
              <button onClick={() => removeAt("contact.people", i)} className="text-[#c0392b]"><Trash2 size={16} /></button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Namn" value={p.name} onChange={(v) => upd(`contact.people.${i}.name`, v)} />
              <Field label="E-post" value={p.email} onChange={(v) => upd(`contact.people.${i}.email`, v)} />
            </div>
            <Field label="Notering" value={p.note} onChange={(v) => upd(`contact.people.${i}.note`, v)} />
          </div>
        ))}
        <button onClick={() => pushTo("contact.people", { name: "", email: "", note: "" })} className="inline-flex items-center gap-2 text-sm text-[#1A3B2A] font-semibold"><Plus size={15} /> Lägg till person</button>
      </Card>
    </div>
  );
};

const Applications = () => {
  const [items, setItems] = useState(null);

  const load = useCallback(() => {
    fetch(`${API}/applications`, { credentials: "include" })
      .then((r) => r.json())
      .then(setItems)
      .catch(() => setItems([]));
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggle = async (id, handled) => {
    await fetch(`${API}/applications/${id}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ handled }),
    });
    load();
  };
  const remove = async (id) => {
    if (!window.confirm("Ta bort ansökan?")) return;
    await fetch(`${API}/applications/${id}`, { method: "DELETE", credentials: "include" });
    load();
  };

  if (!items) return <p className="text-[#4A6D56] flex items-center gap-2"><Loader2 className="animate-spin" size={18} /> Laddar ansökningar…</p>;
  if (items.length === 0)
    return (
      <div className="rounded-2xl border border-dashed border-[#D1D8D3] p-16 text-center text-[#4A6D56]">
        <Inbox className="mx-auto mb-3" /> Inga ansökningar än.
      </div>
    );

  return (
    <div className="grid gap-4" data-testid="applications-list">
      {items.map((a) => (
        <div key={a.id} className={`rounded-2xl border p-5 ${a.handled ? "border-[#D1D8D3] bg-[#EEF2EE]" : "border-[#1A3B2A]/30 bg-white"}`}>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <p className="font-display text-2xl text-[#1A3B2A]">{a.name}</p>
              <p className="text-sm text-[#4A6D56]">
                <a href={`mailto:${a.email}`} className="underline">{a.email}</a>
                {a.phone && ` · ${a.phone}`}
              </p>
              <span className="inline-block mt-2 rounded-full bg-[#C2D3C4] text-[#1A3B2A] text-xs font-semibold px-3 py-1 uppercase tracking-wider">{a.member_type}</span>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={() => toggle(a.id, !a.handled)} className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold ${a.handled ? "bg-[#4A6D56] text-white" : "border border-[#1A3B2A] text-[#1A3B2A]"}`}>
                <Check size={14} /> {a.handled ? "Hanterad" : "Markera hanterad"}
              </button>
              <button onClick={() => remove(a.id)} className="text-[#c0392b]"><Trash2 size={16} /></button>
            </div>
          </div>
          {a.message && <p className="text-[#1B1F1C]/70 mt-3 text-sm border-t border-[#E2E8E4] pt-3">{a.message}</p>}
          <p className="text-xs text-[#1B1F1C]/40 mt-3">{new Date(a.created_at).toLocaleString("sv-SE")}</p>
        </div>
      ))}
    </div>
  );
};

export default function Admin() {
  const { user, loading, login, logout } = useAuth();
  const [tab, setTab] = useState("content");

  useEffect(() => {
    if (!loading && !user) login();
  }, [loading, user, login]);

  if (loading || !user)
    return <div className="min-h-screen grid place-items-center bg-[#F7F7F4] text-[#1A3B2A]"><p className="font-display text-3xl">Laddar…</p></div>;

  if (!user.is_admin)
    return (
      <div className="min-h-screen grid place-items-center bg-[#F7F7F4] text-center px-6">
        <div className="max-w-md">
          <ShieldCheck className="mx-auto text-[#4A6D56] mb-4" size={40} />
          <h1 className="font-display text-4xl text-[#1A3B2A]">Endast för administratörer</h1>
          <p className="text-[#1B1F1C]/70 mt-4">
            Du är inloggad som <strong data-testid="admin-current-email">{user.email}</strong>, men det kontot har inte adminbehörighet.
          </p>
          <p className="text-[#1B1F1C]/60 mt-2 text-sm">
            Logga ut och logga in igen med ett administratörskonto, eller be om att din adress läggs till som admin.
          </p>
          <div className="mt-8 flex items-center justify-center gap-3">
            <button
              data-testid="admin-switch-logout-btn"
              onClick={logout}
              className="inline-flex items-center gap-2 rounded-full bg-[#1A3B2A] text-[#F7F7F4] px-6 py-3 text-sm font-bold hover:brightness-125 active:scale-95 transition-[filter,transform] duration-300"
            >
              <LogOut size={16} /> Logga ut & byt konto
            </button>
            <a href="/" className="inline-flex items-center gap-2 text-[#1A3B2A] font-semibold"><ArrowLeft size={16} /> Startsidan</a>
          </div>
        </div>
      </div>
    );

  return (
    <main className="min-h-screen bg-[#F7F7F4]">
      <header className="bg-[#1A3B2A] text-[#F7F7F4]">
        <div className="max-w-4xl mx-auto px-6 py-5 flex items-center justify-between">
          <a href="/" className="flex items-center gap-3">
            <span className="grid place-items-center h-10 w-10 rounded-full bg-white overflow-hidden">
              <img src={LOGO_URL} alt="logotyp" className="h-8 w-8 object-contain" />
            </span>
            <span className="font-display text-2xl">Admin</span>
          </a>
          <button data-testid="admin-logout-btn" onClick={logout} className="inline-flex items-center gap-2 rounded-full border border-white/30 px-4 py-2 text-sm hover:bg-white/10 transition-colors">
            <LogOut size={16} /> Logga ut
          </button>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-6 py-8">
        <div className="flex gap-2 mb-8">
          <button data-testid="admin-tab-content" onClick={() => setTab("content")} className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold transition-colors ${tab === "content" ? "bg-[#1A3B2A] text-[#F7F7F4]" : "bg-white border border-[#D1D8D3] text-[#1A3B2A]"}`}>
            <FileText size={16} /> Innehåll
          </button>
          <button data-testid="admin-tab-applications" onClick={() => setTab("applications")} className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold transition-colors ${tab === "applications" ? "bg-[#1A3B2A] text-[#F7F7F4]" : "bg-white border border-[#D1D8D3] text-[#1A3B2A]"}`}>
            <Inbox size={16} /> Ansökningar
          </button>
        </div>
        {tab === "content" ? <ContentEditor /> : <Applications />}
      </div>
    </main>
  );
}
